// @flow
import * as React from 'react';
import classNames from 'classnames';
import * as Yup from 'yup';
import { Formik, Field, Form } from 'formik';

// Types
import { gtmUserEventType } from 'types/gtm';
// eslint-disable-next-line import/named
import type { IntlShape } from 'react-intl';

// Components
import { Container, Row, Col } from 'reactstrap';
import InputField from 'components/atoms/InputField';
import InfoPanel from 'components/atoms/InfoPanel';
import LicenseNumberInput from 'components/molecules/LicenseNumberInput';

// utils
import {
  isPhoneNumberValid,
  formatPhoneNumber,
  rawPhoneNumber
} from 'utils/string';
import { EMAIL_REGEX } from 'utils/regex';
import { isObjEmpty } from 'utils/varValidation';

// Constants
import { APP_FLOW } from 'constants/appFlow';
import { APPOINTMENT_STATUS } from 'constants/appointmentStatus';

import './ContactInfo.scss';

const ns = 'contact-info';
const { LOG_ME_IN } = APP_FLOW;
const { NEW } = APPOINTMENT_STATUS;

type ContactInfoProps = {
  firstName: string,
  lastName: string,
  phone: string,
  email: string
};

type Props = {
  mobilePhone: string,
  setFirstName: string => void,
  setLastName: string => void,
  setEmail: string => void,
  setPhoneNumber: string => void,
  setMobilePhoneNumber: string => void,
  intl: IntlShape,
  isModifyEnabledFindMe?: boolean,
  vehicle: Object,
  setObfuscatedBy: null => void,
  resetObfuscatedContactInfo: () => void,
  appointmentStatus: string,
  isObfuscated: boolean,
  isDmsAppt: boolean,
  obfuscatedBy: string,
  gtmUserEvent: gtmUserEventType,
  appFlow: string,
  dealerCountryCode: string,
  contactInfo: ContactInfoProps,
  setContactInfoFormValid: boolean => void,
  isContactInfoFormValid: boolean
};

const validationSchema = (props: Props) => {
  return Yup.object().shape({
    firstName: Yup.string().required(),
    lastName: Yup.string().required(),
    phone: Yup.string()
      .required()
      .test({
        name: 'phone',
        message: 'Phone number is not valid',
        test(value?: string = '') {
          return props.obfuscatedBy === 'email' ||
            props.obfuscatedBy === 'licenseNumber' ||
            props.obfuscatedBy === 'deeplink'
            ? true
            : isPhoneNumberValid(value, props.dealerCountryCode);
        }
      }),
    email: Yup.string()
      .required()
      .test({
        name: 'email',
        message: 'Email is not valid',
        test(value?: string = '') {
          return props.obfuscatedBy !== 'email'
            ? true
            : EMAIL_REGEX.test(value || '');
        }
      })
  });
};

export default class ContactInfo extends React.Component<Props> {
  constructor(props: Props) {
    super(props);
    (this: any).form = React.createRef();
    this.onInputChange = this.onInputChange.bind(this);
  }

  componentDidMount() {
    (this: any).form.current.getFormikActions().validateForm();
  }

  form: React.Ref<any>;

  phoneValidate = (value: string) => {
    const { dealerCountryCode, isObfuscated, obfuscatedBy } = this.props;
    return (!isObfuscated || obfuscatedBy === 'phoneNumber') &&
      !isPhoneNumberValid(value, dealerCountryCode)
      ? 'Invalid phone number'
      : undefined;
  };

  emailValidate = value => {
    const { isObfuscated, obfuscatedBy } = this.props;
    return (
      (!isObfuscated || obfuscatedBy === 'email') &&
      !EMAIL_REGEX.test(value || '') &&
      'Invalid email address'
    );
  };

  renderInputField = (f: any) => {
    const { dealerCountryCode, isObfuscated, obfuscatedBy, intl } = this.props;
    const {
      type,
      field: { value, name },
      className,
      form: { errors }
    } = f;
    let errorField = false;
    let iconAriaLabel = intl.formatMessage({
      id: 'c8.aria.contact.email.address',
      defaultMessage: 'Contact email address'
    });

    if (name === 'phone') {
      const phoneNumber = value || '';
      iconAriaLabel = intl.formatMessage({
        id: 'c8.aria.contact.phone.number',
        defaultMessage: 'Contact phone number'
      });
      if (
        isObfuscated &&
        (obfuscatedBy === 'email' ||
          obfuscatedBy === 'licenseNumber' ||
          obfuscatedBy === 'deeplink')
      )
        f.field.value = phoneNumber;
      else {
        if (isPhoneNumberValid(phoneNumber, dealerCountryCode)) {
          f.field.value = formatPhoneNumber(phoneNumber, dealerCountryCode);
        } else {
          f.field.value = rawPhoneNumber(phoneNumber);
          errorField = true;
        }
      }
    } else if (name === 'email') {
      if (isObfuscated && obfuscatedBy !== 'email') {
        f.field.value = value;
      } else {
        if (this.emailValidate(value)) {
          errorField = true;
        }
      }
    }

    const isFormikInvalid = Boolean(
      !value || errorField || (errors && errors[name])
    );
    const classProps = classNames(className, {
      invalid: isFormikInvalid
    });

    return (
      <InputField
        className={classProps}
        placeholder={f.placeholder}
        leftAddonConf={f.leftAddonConf}
        type={type || 'text'}
        inputConf={{
          ...f.field,
          onChange: e => {
            this.onInputChange(name)(e);
            f.field.onChange(e);
          },
          onBlur: e => {
            if (name === 'email' || name === 'phone') {
              this.onInputBlur(e, name);
            }
          }
        }}
        iconAriaLabel={iconAriaLabel}
      />
    );
  };

  onInputChange = (field: string) => {
    return (event: any) => {
      const {
        setFirstName,
        setLastName,
        setEmail,
        setPhoneNumber,
        isObfuscated,
        resetObfuscatedContactInfo,
        setObfuscatedBy
      } = this.props;
      const { value } = event.target;
      let inputValue;
      if (!isObfuscated) {
        inputValue = value;
      } else {
        inputValue = event.nativeEvent.data;
        event.target.value = inputValue;
        resetObfuscatedContactInfo();
        setObfuscatedBy(null);
      }
      switch (field) {
        case 'firstName':
          setFirstName(inputValue);
          break;
        case 'lastName':
          setLastName(inputValue);
          break;
        case 'email':
          setEmail(inputValue);
          break;
        case 'phone':
          setPhoneNumber(rawPhoneNumber(inputValue));
          break;
        default:
          break;
      }
    };
  };

  onInputBlur = (e: SyntheticInputEvent<HTMLInputElement>, field: string) => {
    const { gtmUserEvent } = this.props;
    const value = e.target.value;
    const gtmInvalidField = field => {
      gtmUserEvent(
        `Contact Info ${field} Input`,
        'Typed',
        `Invalid ${field}`,
        'GTM_CURRENT_PAGE > Contact Info'
      );
    };
    if (field === 'phone' && this.phoneValidate(value)) {
      gtmInvalidField('Phone Number');
    }
    if (field === 'email' && this.emailValidate(value)) {
      gtmInvalidField('Email');
    }
  };

  render() {
    const {
      isContactInfoFormValid,
      intl,
      contactInfo,
      contactInfo: { firstName, lastName, phone, email },
      appFlow,
      isObfuscated,
      appointmentStatus,
      mobilePhone,
      vehicle: { licenseNumber },
      setContactInfoFormValid,
      isDmsAppt
    } = this.props;
    const isOnlyView =
      (isContactInfoFormValid && appointmentStatus !== NEW && isObfuscated) ||
      (isContactInfoFormValid && appointmentStatus !== NEW && isDmsAppt) ||
      (isContactInfoFormValid && appFlow === LOG_ME_IN);
    const contactFormClasses = classNames(`${ns}__contact-form`, {
      'd-none': isOnlyView,
      'contact-info-wrapped': true
    });
    const contactInfoKey = `${firstName},${lastName},${phone},${email},${mobilePhone},${licenseNumber}`;
    return (
      <Formik
        ref={this.form}
        key={isObfuscated ? contactInfoKey : ''}
        initialValues={this.props.contactInfo}
        validationSchema={validationSchema(this.props)}
        onSubmit={() => {}}
        render={({ errors }) => {
          setContactInfoFormValid(isObjEmpty(errors));
          return (
            <Container className={`${ns} pb-4`} fluid={true}>
              <Form className={contactFormClasses} onSubmit={() => {}}>
                <Row>
                  <Col xs={12} sm={12} md={6}>
                    <Field
                      name="firstName"
                      className="first-name-input-field"
                      placeholder={intl.formatMessage({
                        id: 'c8.review.first.name'
                      })}
                      component={this.renderInputField}
                    />
                  </Col>
                  <Col xs={12} sm={12} md={6}>
                    <Field
                      name="lastName"
                      className="last-name-input-field"
                      placeholder={intl.formatMessage({
                        id: 'c8.review.last.name'
                      })}
                      component={this.renderInputField}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <Field
                      name="phone"
                      placeholder={intl.formatMessage({
                        id: 'c8.review.phone.no'
                      })}
                      className="contact-form-phone"
                      leftAddonConf={{
                        addonValue: 'phone',
                        isGlyhicon: true
                      }}
                      component={this.renderInputField}
                      validate={this.phoneValidate}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <Field
                      name="email"
                      placeholder={intl.formatMessage({
                        id: 'c8.common.email'
                      })}
                      className="contact-form-email"
                      type={'email'}
                      leftAddonConf={{
                        addonValue: 'email',
                        isGlyhicon: true
                      }}
                      component={this.renderInputField}
                      validate={this.emailValidate}
                    />
                  </Col>
                </Row>
              </Form>
              {isOnlyView && <InfoPanel contactInfo={contactInfo} />}
              <LicenseNumberInput onlyView={isOnlyView} />
            </Container>
          );
        }}
      />
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/molecules/ContactInfo/ContactInfo.js